import React, { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { FaCheckCircle, FaFileInvoice, FaSearch, FaTimesCircle } from 'react-icons/fa'
import { FiFile } from 'react-icons/fi'
import { MdOutlineReceiptLong } from 'react-icons/md'
import { useAuth } from '../../provider/auth.provider'
import api from '../../libs/axios'
import { Pagination } from '../../utils/pagination.utils'
import { SelectWithSearch } from '../../utils/select-with-search'
import type { SelectOption } from '../../utils/select-with-search'
import toast from 'react-hot-toast'
import { useDebouncedCallback } from '../../hooks/useDebounce'

interface Invoice {
  id: number
  po_number: string
  po_date: string
  po_document?: string
  item: string
  item_description: string
  invoice_number?: string
  invoice_date?: string
  invoice_document?: string
  invoice_basic_amount?: number | null
  invoice_gst_amount?: number | null
  invoice_total_amount?: number | null
  working_month?: string | null
  status: string
  rejection_reason?: string | null
  vendor?: { id: number; vendor_full_name: string; vendor_code?: string }
  billing_coordinator?: { id: number; first_name: string; last_name: string }
  project_manager?: { id: number; first_name: string; last_name: string } | null
  team_coordinator?: { id: number; first_name: string; last_name: string } | null
  site?: {
    id: number
    site_id?: string
    site_name?: string
    reference_number?: string
    team_coordinator?: { id: number; first_name: string; last_name: string } | null
    project?: { id: number; project_name: string; activity?: { id: number; activity_name: string } }
  }
}

interface PaginationData {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next_page: boolean
  has_prev_page: boolean
}

// No status filter selected → the server returns every stage, PO Generated included.
const STATUS_OPTIONS = [
  { value: '', label: 'All Stages' },
  { value: 'po_generated', label: 'PO Generated' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'invoice_booked', label: 'Invoice Booked' },
  { value: 'invoice_rejected', label: 'Invoice Rejected' },
]

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  po_not_required: { label: 'PO Not Required', color: 'bg-orange-100 text-orange-700' },
  po_generated: { label: 'PO Generated', color: 'bg-blue-100 text-blue-700' },
  invoice_submitted: { label: 'Invoice Submitted', color: 'bg-amber-100 text-amber-700' },
  invoice_booked: { label: 'Invoice Booked', color: 'bg-green-100 text-green-700' },
  invoice_rejected: { label: 'Invoice Rejected', color: 'bg-red-100 text-red-700' },
}

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtAmt = (v: number | null | undefined) =>
  v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'
const fullName = (u?: { first_name: string; last_name: string } | null) =>
  u ? `${u.first_name} ${u.last_name}` : '—'

// Columns identical to the Vendor Portal "Submitted Invoices" table (item 4),
// preceded by a selection checkbox for the accept/reject workflow. The invoice
// document has its own column rather than sharing Action (item 3).
const HEADERS = [
  'Action', 'Invoice Document', 'PO Date', 'PO Number', 'Project Name', 'Activity', 'Vendor Name',
  'Billing Coordinator', 'Invoice Item Name', 'Invoice Description',
  'Working Month', 'Vendor Invoice Number', 'Vendor Invoice Date',
  'Project Manager', 'Team Coordinator', 'Invoice Stage',
  'Basic Amount', 'GST Amount', 'Total Amount', 'Rejection Reason',
]

// ── Reject modal ──────────────────────────────────────────────────────────────
interface RejectModalProps {
  isOpen: boolean
  count: number
  onClose: () => void
  onConfirm: (reason: string) => void
  rejecting: boolean
}

const RejectModal: React.FC<RejectModalProps> = ({ isOpen, count, onClose, onConfirm, rejecting }) => {
  const [reason, setReason] = useState('')
  useEffect(() => { if (isOpen) setReason('') }, [isOpen])
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Reject Invoice{count !== 1 ? 's' : ''}</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            Reject <strong>{count}</strong> invoice{count !== 1 ? 's' : ''}? The vendor will need to resubmit.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rejection Reason <span className="text-red-500">*</span></label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 text-sm"
              rows={3}
              placeholder="Explain why the invoice is being rejected..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">This reason will be shown to the vendor.</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={rejecting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={() => reason.trim() ? onConfirm(reason.trim()) : undefined} disabled={rejecting || !reason.trim()}
            className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
            {rejecting
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Rejecting...</>
              : <><FaTimesCircle className="w-4 h-4" />Reject</>}
          </button>
        </div>
      </div>
    </div>
  )
}

const VendorInvoiceAcceptance: React.FC = () => {
  const { hasPermission } = useAuth()
  const canAccept = hasPermission('VendorInvoicing', 'accept_vendor_invoice')

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total_count: 0, total_pages: 1, has_next_page: false, has_prev_page: false })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [vendorFilter, setVendorFilter] = useState<SelectOption | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [booking, setBooking] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [confirmBookOpen, setConfirmBookOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)

  const fetchInvoices = useCallback(async (overrides: { page?: number; limit?: number; search?: string; status?: string; vendor_id?: string } = {}) => {
    setLoading(true)
    try {
      const st = overrides.status !== undefined ? overrides.status : statusFilter
      const params: any = {
        page: overrides.page ?? pagination.page,
        limit: overrides.limit ?? pagination.limit,
      }
      // Omit `status` entirely when nothing is selected so every stage comes back.
      if (st) params.status = st
      const s = overrides.search !== undefined ? overrides.search : search
      if (s) params.search = s
      const vid = overrides.vendor_id !== undefined ? overrides.vendor_id : vendorFilter?.value
      if (vid) params.vendor_id = vid

      const res = await api.get('/billing/vendor-site-invoices', { params })
      if (res.data.success) {
        setInvoices(res.data.data.rows || [])
        setPagination(res.data.data.pagination || pagination)
        setSelected(new Set())
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit, search, statusFilter, vendorFilter])

  useEffect(() => { fetchInvoices({ page: 1 }) }, [])

  const debouncedSearch = useDebouncedCallback((val: string) => fetchInvoices({ search: val, page: 1 }), 400)

  // Only submitted invoices can be booked / rejected.
  const selectableInvoices = invoices.filter((i) => i.status === 'invoice_submitted')
  const allSelected = selectableInvoices.length > 0 && selectableInvoices.every((inv) => selected.has(inv.id))

  const toggleInvoice = (inv: Invoice) => {
    if (inv.status !== 'invoice_submitted') return
    setSelected((prev) => { const n = new Set(prev); n.has(inv.id) ? n.delete(inv.id) : n.add(inv.id); return n })
  }

  const handleSelectAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(selectableInvoices.map((i) => i.id)))
  }

  const handleBook = async () => {
    setBooking(true)
    try {
      await api.patch('/billing/vendor-site-invoices/book', { ids: Array.from(selected) })
      toast.success(`${selected.size} invoice(s) booked successfully`)
      setConfirmBookOpen(false)
      setSelected(new Set())
      fetchInvoices({ page: 1 })
    } catch {
      // toast handled by interceptor
    } finally {
      setBooking(false)
    }
  }

  const handleReject = async (reason: string) => {
    setRejecting(true)
    try {
      await api.patch('/billing/vendor-site-invoices/reject', { ids: Array.from(selected), rejection_reason: reason || undefined })
      toast.success(`${selected.size} invoice(s) rejected`)
      setRejectModalOpen(false)
      setSelected(new Set())
      fetchInvoices({ page: 1 })
    } catch {
      // toast handled by interceptor
    } finally {
      setRejecting(false)
    }
  }

  return (
    <>
      <Helmet><title>Vendor Invoice Acceptance</title></Helmet>

      {confirmBookOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Invoice Booking</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Mark <strong>{selected.size}</strong> invoice{selected.size !== 1 ? 's' : ''} as <span className="font-semibold text-green-700">Invoice Booked</span>?</p>
              <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmBookOpen(false)} disabled={booking}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={handleBook} disabled={booking}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {booking ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Booking...</> : <><FaCheckCircle className="w-4 h-4" />Confirm Book</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <RejectModal
        isOpen={rejectModalOpen}
        count={selected.size}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleReject}
        rejecting={rejecting}
      />

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <MdOutlineReceiptLong className="w-6 h-6 text-blue-700" />Vendor Invoice Acceptance
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">Review vendor POs and invoices at every stage</p>
            </div>
            {selected.size > 0 && canAccept && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRejectModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors shadow-sm"
                >
                  <FaTimesCircle className="w-4 h-4" />Reject ({selected.size})
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmBookOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors shadow-sm"
                >
                  <FaCheckCircle className="w-4 h-4" />Mark as Invoice Booked ({selected.size})
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50/30 flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="flex-1 max-w-md relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by PO number, site ID..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); debouncedSearch(e.target.value) }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); fetchInvoices({ status: e.target.value, page: 1 }) }}
            className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="w-full sm:w-64">
            <SelectWithSearch
              placeholder="Filter by vendor..."
              value={vendorFilter}
              onChange={(opt) => { setVendorFilter(opt); fetchInvoices({ vendor_id: opt ? String(opt.value) : '', page: 1 }) }}
              apiEndpoint="/vendor/list"
              dataKey="vendors"
              labelKey="vendor_full_name"
              valueKey="id"
              searchParam="search"
              className="w-full"
            />
          </div>
        </div>

        {selected.size > 0 && (
          <div className="px-4 sm:px-6 py-2 bg-green-50 border-b border-green-100 flex items-center justify-between">
            <span className="text-sm text-green-700 font-medium">{selected.size} invoice{selected.size !== 1 ? 's' : ''} selected</span>
            <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-green-600 hover:text-green-800 underline">Clear</button>
          </div>
        )}

        {/* Table — mirrors Vendor Portal Submitted Invoices columns (item 4) */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {/* {canAccept && (
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={allSelected && selectableInvoices.length > 0} onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" title="Select all submitted invoices" />
                  </th>
                )} */}
                {HEADERS.map((h) => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>{[...Array(canAccept ? HEADERS.length + 1 : HEADERS.length)].map((_, j) => <td key={j} className="px-3 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
                ))
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={canAccept ? HEADERS.length + 1 : HEADERS.length} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                        <FaFileInvoice className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No invoices found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => {
                  const isChecked = selected.has(inv.id)
                  const selectable = inv.status === 'invoice_submitted'
                  return (
                    <tr key={inv.id} className={`hover:bg-gray-50 transition-colors ${isChecked ? 'bg-green-50' : ''}`}>
                      {/* Action = selection checkbox + the PO doc link */}
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          {canAccept && (
                            <input type="checkbox" checked={isChecked} onChange={() => toggleInvoice(inv)} disabled={!selectable}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                              title={selectable ? 'Select' : 'Only submitted invoices can be actioned'} />
                          )}
                          {inv.po_document ? (
                            <a href={inv.po_document} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline whitespace-nowrap" title="View Vendor PO Document">
                              <FiFile className="w-3 h-3" /> PO Doc
                            </a>
                          ) : null}
                        </div>
                      </td>
                      {/* Invoice Document — its own column (item 3) */}
                      <td className="px-3 py-3">
                        {inv.invoice_document ? (
                          <a href={inv.invoice_document} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline whitespace-nowrap" title="View Vendor Invoice Document">
                            <FiFile className="w-3 h-3" /> Invoice Doc
                          </a>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-gray-600 whitespace-nowrap text-xs">{fmt(inv.po_date)}</td>
                      <td className="px-3 py-3 font-mono font-semibold text-blue-700 text-xs whitespace-nowrap">{inv.po_number}</td>
                      <td className="px-3 py-3 text-gray-900 text-xs whitespace-nowrap">{inv.site?.project?.project_name || '—'}</td>
                      <td className="px-3 py-3 text-gray-500 text-xs">{inv.site?.project?.activity?.activity_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">
                        {inv.vendor ? (
                          <div>
                            <div>{inv.vendor.vendor_full_name}</div>
                            {inv.vendor.vendor_code && <div className="text-gray-400 text-xs">{inv.vendor.vendor_code}</div>}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fullName(inv.billing_coordinator)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 font-medium">{inv.item || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-500 max-w-[160px] truncate" title={inv.item_description || ''}>{inv.item_description || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{inv.working_month || '—'}</td>
                      <td className="px-3 py-3 font-mono text-xs text-gray-900">{inv.invoice_number || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">{fmt(inv.invoice_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fullName(inv.project_manager)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fullName(inv.team_coordinator ?? inv.site?.team_coordinator)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[inv.status]?.color ?? 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_CONFIG[inv.status]?.label ?? inv.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtAmt(inv.invoice_basic_amount)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtAmt(inv.invoice_gst_amount)}</td>
                      <td className="px-3 py-3 text-xs font-medium text-gray-900 whitespace-nowrap">{fmtAmt(inv.invoice_total_amount)}</td>
                      <td className="px-3 py-3 text-xs max-w-[200px]">
                        {inv.status === 'invoice_rejected' && inv.rejection_reason ? (
                          <span className="text-red-600" title={inv.rejection_reason}>{inv.rejection_reason}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          pagination={pagination as any}
          onPageChange={(p) => fetchInvoices({ page: p })}
          onLimitChange={(l) => { setPagination((prev) => ({ ...prev, limit: l })); fetchInvoices({ page: 1, limit: l }) }}
        />
      </div>
    </>
  )
}

export default VendorInvoiceAcceptance
